import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SERVICES, CONTACT_INFO } from '../data';
import ReviewSlider from '../components/ReviewSlider';
import { getResizedImageUrl, updateMetaTags } from '../lib/utils';

// Mapping of service slugs to background hover images from the scrape assets
const SERVICE_IMAGES = {
  'haveservice': 'https://assets.auxodev.dk/assets/340cd821-77c4-4f44-a38b-9945cfcac15b/haveservice-lawn.jpg',
  'anlaeg-af-have': 'https://assets.auxodev.dk/assets/340cd821-77c4-4f44-a38b-9945cfcac15b/hero-danish-garden.jpg',
  'belaegning': 'https://assets.auxodev.dk/assets/340cd821-77c4-4f44-a38b-9945cfcac15b/vedligeholdelse-garden.jpg',
  'beskaering': 'https://assets.auxodev.dk/assets/340cd821-77c4-4f44-a38b-9945cfcac15b/beskaering-tree.jpg',
  'haekklipning': 'https://assets.auxodev.dk/assets/340cd821-77c4-4f44-a38b-9945cfcac15b/haekklipning-hedge.jpg',
  'vedligeholdelse': 'https://assets.auxodev.dk/assets/340cd821-77c4-4f44-a38b-9945cfcac15b/anlaeg-have.jpg',
};

export default function Home() {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(null);

  useEffect(() => {
    // Fetch galleries and page metadata from CMS
    const fetchGalleries = fetch('https://api.dev.auxo.dk/public/content/galleri')
      .then(res => res.json())
      .catch(err => {
        console.error('Fejl ved hentning af galleri:', err);
        return [];
      });

    const fetchPageData = fetch('https://api.dev.auxo.dk/public/v2/content/sider?page_slug=forside')
      .then(res => res.json())
      .then(data => data.items?.[0] || null)
      .catch(err => {
        console.error('Fejl ved hentning af forside-data:', err);
        return null;
      });

    Promise.all([fetchGalleries, fetchPageData])
      .then(([galleriesData, pageData]) => {
        setGalleries(galleriesData || []);
        
        // Update meta tags
        const metaTitle = pageData?.meta_title?.da || 'Haveglæde v/Michael Poul Ewers Petersen';
        const metaDesc = pageData?.meta_description?.da || 'Professionel haveservice i Esbjerg og omegn. Vi klipper din hæk, beskærer dine træer, luger dit ukrudt, slår dit græs og meget mere.';
        updateMetaTags(metaTitle, metaDesc);
        
        setLoading(false);
      });
  }, []);

  // Extract all images from all galleries for the feed
  const allProjectImages = galleries.flatMap(g => 
    (g.images || []).map(imgUrl => ({
      url: imgUrl,
      title: g.title?.da || 'Udført arbejde',
      category: g.title?.da || 'Havearbejde'
    }))
  ).slice(0, 6); // Show top 6 images on front page

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative h-[80vh] min-h-[500px] w-full overflow-hidden bg-primary-green">
        {/* Background Image */}
        <img 
          src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/340cd821-77c4-4f44-a38b-9945cfcac15b/hero-danish-garden.jpg" 
          alt="Moderne dansk have" 
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        {/* Dark Green Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-primary-green via-primary-green/60 to-transparent" />

        {/* Decorative Background Leaves */}
        <div className="absolute inset-0 pointer-events-none opacity-10 flex justify-between items-center px-12 overflow-hidden z-10">
          <i className="fa-solid fa-leaf text-white text-5xl animate-float-leaf-1 transform translate-y-12" />
          <i className="fa-solid fa-leaf text-white text-4xl animate-float-leaf-2 transform -translate-y-12 hidden md:block" />
          <i className="fa-solid fa-leaf text-white text-6xl animate-float-leaf-3 transform translate-y-6 hidden lg:block" />
        </div>

        {/* Content */}
        <div className="relative z-20 mx-auto flex h-full max-w-7xl flex-col justify-center px-4 sm:px-6 lg:px-8 text-white">
          <div className="max-w-3xl space-y-6">
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-secondary-green bg-white/10 px-3 py-1.5 rounded-sm inline-block">
              Professionel Haveservice i Esbjerg & Omegn
            </span>
            <h1 className="font-display text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight">
              Vi passer din have som du gerne vil have det
            </h1>
            <p className="font-body text-sm sm:text-base text-gray-200 leading-relaxed max-w-2xl">
              Hos haveglæde klipper vi din hæk, beskærer dine træer, luger dit ukrudt, slår dit græs og meget mere.
            </p>
            <div className="flex flex-wrap gap-4 pt-4">
              <Link 
                to="/kontakt" 
                className="bg-accent-orange hover:bg-accent-orange-dark text-white font-display text-xs font-bold px-8 py-4 uppercase tracking-wider transition-all duration-200 rounded-sm shadow-md hover:scale-[1.02]"
              >
                Kontakt os
              </Link>
              <Link 
                to="/referencer" 
                className="bg-white/10 hover:bg-white/20 text-white border border-white/30 font-display text-xs font-bold px-8 py-4 uppercase tracking-wider transition-all duration-200 rounded-sm backdrop-blur-xs hover:scale-[1.02]"
              >
                Se vores referencer
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Presentation Section */}
      <section className="py-16 md:py-24 bg-white relative overflow-hidden">
        {/* Decorative Background Leaves */}
        <div className="absolute inset-0 pointer-events-none opacity-5 flex justify-between items-center px-12 overflow-hidden">
          <i className="fa-solid fa-leaf text-primary-green text-5xl animate-float-leaf-1 transform translate-y-12" />
          <i className="fa-solid fa-leaf text-primary-green text-4xl animate-float-leaf-2 transform -translate-y-12 hidden md:block" />
          <i className="fa-solid fa-leaf text-primary-green text-6xl animate-float-leaf-3 transform translate-y-6 hidden lg:block" />
        </div>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <span className="text-xs font-bold uppercase tracking-widest text-secondary-green block">
                Hvem er vi?
              </span>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-primary-green">
                Professionel haveservice til ethvert behov
              </h2>
              <p className="font-body text-sm text-neutral-muted leading-relaxed">
                Haveglæde laver service til din have hele sæsonen. Vi passer din have som du gerne vil have det, så du får glæde af din have. Vi kommer med råd og vejledning, så du har bedre mulighed for at bestemme hvordan din have skal være.
              </p>
              <p className="font-body text-sm text-neutral-muted leading-relaxed">
                Hvis du ikke har mulighed for at passe din have, det kan være på grund af alder, fysik, manglende tid og lyst, så ring eller skriv en mail eller sms, så kommer vi og hjælper. Vi passer også din have mens du er på ferie.
              </p>
              <div className="pt-4">
                <Link 
                  to="/om-haveglaede" 
                  className="text-xs font-bold uppercase tracking-wider text-secondary-green hover:text-primary-green transition-colors inline-flex items-center gap-2"
                >
                  Læs mere om Haveglæde
                  <i className="fa-solid fa-arrow-right" />
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/340cd821-77c4-4f44-a38b-9945cfcac15b/vedligeholdelse-garden.jpg" 
                alt="Havearbejde og vedligeholdelse" 
                className="w-full h-[500px] md:h-[550px] object-cover rounded-sm shadow-md"
              />
              <div className="absolute -bottom-6 sm:-left-2 md:left-2 lg:-left-6 bg-secondary-green text-white p-6 rounded-sm shadow-lg hidden sm:block max-w-xs">
                <p className="font-display text-sm font-bold uppercase tracking-wider mb-1">Mange års erfaring</p>
                <p className="text-xs text-gray-100">Vi leverer altid et professionelt og tilfredsstillende resultat.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-neutral-light-bg relative overflow-hidden">
        {/* Decorative Background Leaves */}
        <div className="absolute inset-0 pointer-events-none opacity-5 flex justify-between items-center px-12 overflow-hidden">
          <i className="fa-solid fa-leaf text-primary-green text-5xl animate-float-leaf-1 transform translate-y-12" />
          <i className="fa-solid fa-leaf text-primary-green text-4xl animate-float-leaf-2 transform -translate-y-12 hidden md:block" />
          <i className="fa-solid fa-leaf text-primary-green text-6xl animate-float-leaf-3 transform translate-y-6 hidden lg:block" />
        </div>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-xs font-bold uppercase tracking-widest text-secondary-green block">
              Vores kompetencer
            </span>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-primary-green">
              Hvad kan vi tilbyde?
            </h2>
            <p className="font-body text-sm text-neutral-muted leading-relaxed">
              Haveglæde udfører det meste havearbejde, bare spørg. Vi står klar til at hjælpe dig med alt fra hækklipning til nyanlæg.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {SERVICES.map((service) => (
              <div 
                key={service.slug}
                className="bg-white p-8 rounded-sm border border-gray-100 shadow-xs flex flex-col justify-between transition-all duration-300 hover:shadow-md relative overflow-hidden group"
              >
                {/* Hover Background Image */}
                <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-10 transition-opacity duration-500 z-0">
                  <img 
                    src={getResizedImageUrl(SERVICE_IMAGES[service.slug] || 'https://assets.auxodev.dk/assets/340cd821-77c4-4f44-a38b-9945cfcac15b/vt_1.webp', 'width=400,quality=70,format=auto')} 
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="space-y-4 relative z-10">
                  <div className="w-12 h-12 rounded-full bg-secondary-green/10 flex items-center justify-center text-secondary-green text-xl group-hover:scale-110 transition-transform duration-300">
                    <i className={service.icon} />
                  </div>
                  <h3 className="font-display text-base font-bold uppercase tracking-wider text-primary-green">
                    {service.title}
                  </h3>
                  <p className="font-body text-xs text-neutral-muted leading-relaxed">
                    {service.shortDesc}
                  </p>
                </div>
                <div className="mt-6 pt-4 border-t border-gray-50 relative z-10">
                  <Link 
                    to={`/ydelser/${service.slug}`} 
                    className="text-xs font-bold uppercase tracking-wider text-secondary-green hover:text-primary-green transition-colors inline-flex items-center gap-1.5"
                  >
                    Læs mere
                    <i className="fa-solid fa-chevron-right text-[10px]" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 md:py-24 bg-white relative overflow-hidden">
        {/* Decorative Background Leaves */}
        <div className="absolute inset-0 pointer-events-none opacity-5 flex justify-between items-center px-12 overflow-hidden">
          <i className="fa-solid fa-leaf text-primary-green text-5xl animate-float-leaf-1 transform translate-y-12" />
          <i className="fa-solid fa-leaf text-primary-green text-4xl animate-float-leaf-2 transform -translate-y-12 hidden md:block" />
          <i className="fa-solid fa-leaf text-primary-green text-6xl animate-float-leaf-3 transform translate-y-6 hidden lg:block" />
        </div>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-xs font-bold uppercase tracking-widest text-secondary-green block">
              Fordele ved Haveglæde
            </span>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-primary-green">
              Hvorfor vælge Haveglæde?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Benefit 1 */}
            <div className="p-6 text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-primary-green text-white flex items-center justify-center text-2xl mx-auto shadow-sm">
                <i className="fa-regular fa-calendar-star" />
              </div>
              <h3 className="font-display text-sm font-bold uppercase tracking-wider text-primary-green">
                Haveservice året rundt
              </h3>
              <p className="font-body text-xs text-neutral-muted leading-relaxed">
                Hos Haveglæde er du sikret en vinterklar have. Vi sørger for at din have overlever en hård og kold vinter.
              </p>
            </div>

            {/* Benefit 2 */}
            <div className="p-6 text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-primary-green text-white flex items-center justify-center text-2xl mx-auto shadow-sm">
                <i className="fa-regular fa-sparkles" />
              </div>
              <h3 className="font-display text-sm font-bold uppercase tracking-wider text-primary-green">
                Alsidig gartner
              </h3>
              <p className="font-body text-xs text-neutral-muted leading-relaxed">
                Vi er klar til at løse de fleste haveopgaver. Uanset hvor lille eller stor opgaven er, står vi klar til at hjælpe.
              </p>
            </div>

            {/* Benefit 3 */}
            <div className="p-6 text-center space-y-4 md:col-span-2 lg:col-span-1">
              <div className="w-16 h-16 rounded-full bg-primary-green text-white flex items-center justify-center text-2xl mx-auto shadow-sm">
                <i className="fa-regular fa-handshake" />
              </div>
              <h3 className="font-display text-sm font-bold uppercase tracking-wider text-primary-green">
                Privat og erhverv
              </h3>
              <p className="font-body text-xs text-neutral-muted leading-relaxed">
                Uanset om du er en privatperson eller repræsenterer et firma, kan Haveglæde komme med et stærkt tilbud.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CMS Gallery Feed Section */}
      <section className="pt-16 pb-12 md:pt-24 md:pb-12 lg:pb-24 bg-neutral-light-bg relative overflow-hidden">
        {/* Decorative Background Leaves */}
        <div className="absolute inset-0 pointer-events-none opacity-5 flex justify-between items-center px-12 overflow-hidden">
          <i className="fa-solid fa-leaf text-primary-green text-5xl animate-float-leaf-1 transform translate-y-12" />
          <i className="fa-solid fa-leaf text-primary-green text-4xl animate-float-leaf-2 transform -translate-y-12 hidden md:block" />
          <i className="fa-solid fa-leaf text-primary-green text-6xl animate-float-leaf-3 transform translate-y-6 hidden lg:block" />
        </div>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-12 space-y-4">
            <span className="text-xs font-bold uppercase tracking-widest text-secondary-green block">
              Udført arbejde
            </span>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-primary-green">
              Vores seneste projekter
            </h2>
            <p className="font-body text-sm text-neutral-muted leading-relaxed">
              Her kan du se et udvalg af vores seneste opgaver udført i Esbjerg og omegn. Billederne hentes direkte fra vores CMS-galleri.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-green" />
            </div>
          ) : allProjectImages.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {allProjectImages.map((img, idx) => (
                <div 
                  key={idx}
                  onClick={() => setActiveImage(img.url)}
                  className="group relative h-64 overflow-hidden rounded-sm bg-gray-100 shadow-xs cursor-pointer transition-all duration-300 hover:shadow-md"
                >
                  <img 
                    src={getResizedImageUrl(img.url, 'width=600,height=400,fit=cover,quality=80,format=auto')} 
                    alt={img.title} 
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-green/80 via-primary-green/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6 text-white" />
                  <div className="absolute bottom-4 left-4 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-secondary-green bg-white px-2 py-1 rounded-xs">
                      {img.category}
                    </span>
                    <h4 className="font-display text-xs font-bold text-white uppercase tracking-wider mt-2">
                      {img.title}
                    </h4>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-xs text-neutral-muted">
              Ingen billeder tilgængelige i øjeblikket.
            </div>
          )}

          <div className="text-center pt-10">
            <Link 
              to="/referencer" 
              className="bg-primary-green hover:bg-primary-green-light text-white font-display text-xs font-bold px-8 py-4 uppercase tracking-wider transition-all duration-200 rounded-sm shadow-sm inline-block"
            >
              Se alle referencer
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews Slider */}
      <ReviewSlider />

      {/* Call To Action Section */}
      <section className="relative py-16 md:py-20 bg-primary-green text-white overflow-hidden">
        <img 
          src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/340cd821-77c4-4f44-a38b-9945cfcac15b/vedligeholdelse-garden.jpg" 
          alt="Grøn have baggrund" 
          className="absolute inset-0 h-full w-full object-cover opacity-15"
        />
        {/* Decorative Background Leaves */}
        <div className="absolute inset-0 pointer-events-none opacity-10 flex justify-between items-center px-12 overflow-hidden z-10">
          <i className="fa-solid fa-leaf text-white text-5xl animate-float-leaf-1 transform translate-y-12" />
          <i className="fa-solid fa-leaf text-white text-4xl animate-float-leaf-2 transform -translate-y-12 hidden md:block" />
          <i className="fa-solid fa-leaf text-white text-6xl animate-float-leaf-3 transform translate-y-6 hidden lg:block" />
        </div>

        <div className="relative z-20 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white">
            Gør noget godt for din have - Kontakt os i dag
          </h2>
          <p className="font-body text-sm text-gray-200 leading-relaxed max-w-2xl mx-auto">
            Uanset om du skal have klippet hækken, omlagt haven eller har brug for en fast aftale om haveservice, står vi klar med et uforpligtende tilbud.
          </p>
          <div className="pt-4 flex flex-wrap justify-center gap-4">
            <Link 
              to="/kontakt" 
              className="bg-accent-orange hover:bg-accent-orange-dark text-white font-display text-xs font-bold px-8 py-4 uppercase tracking-wider transition-all duration-200 rounded-sm shadow-md"
            >
              Få et uforpligtende tilbud
            </Link>
            <a 
              href={`tel:${CONTACT_INFO.phoneRaw}`} 
              className="bg-white text-primary-green hover:bg-neutral-light-bg font-display text-xs font-bold px-8 py-4 uppercase tracking-wider transition-all duration-200 rounded-sm shadow-md whitespace-nowrap"
            >
              Ring op: {CONTACT_INFO.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {activeImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 animate-in fade-in duration-200"
          onClick={() => setActiveImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white text-3xl cursor-pointer hover:text-secondary-green transition-colors"
            onClick={() => setActiveImage(null)}
          >
            <i className="fa-solid fa-xmark" />
          </button>
          <img 
            src={activeImage} 
            alt="Udført arbejde i stor visning" 
            className="max-h-[90vh] max-w-full object-contain rounded-sm shadow-2xl"
          />
        </div>
      )}
    </div>
  );
}
